/**
 * ApiBase - Base API service class for admin portal apps.
 * Handles authentication, token management, and HTTP requests.
 * Extend this class to add app-specific endpoint methods.
 */

class ApiBase {
  constructor(baseURL) {
    this.token = localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.baseURL = baseURL;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  setRefreshToken(refreshToken) {
    this.refreshToken = refreshToken;
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  onRefreshed(token) {
    this.refreshSubscribers.forEach(callback => callback(token));
    this.refreshSubscribers = [];
  }

  addRefreshSubscriber(callback) {
    this.refreshSubscribers.push(callback);
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken })
      });
      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }
      const data = await response.json();
      this.setToken(data.access_token);
      return data.access_token;
    } catch (error) {
      this.setToken(null);
      this.setRefreshToken(null);
      throw error;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...(options.headers || {})
      }
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        if (this.refreshToken && !this.isRefreshing) {
          this.isRefreshing = true;
          try {
            const newToken = await this.refreshAccessToken();
            this.isRefreshing = false;
            this.onRefreshed(newToken);
            config.headers['Authorization'] = `Bearer ${newToken}`;
            const retryResponse = await fetch(url, config);
            if (!retryResponse.ok) {
              const errorData = await retryResponse.json().catch(() => ({}));
              throw new Error(errorData.detail || `HTTP error! status: ${retryResponse.status}`);
            }
            return await retryResponse.json();
          } catch (refreshError) {
            this.isRefreshing = false;
            window.dispatchEvent(new CustomEvent('authenticationFailed', {
              detail: { reason: 'token_expired' }
            }));
            throw new Error('Authentication failed');
          }
        } else if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.addRefreshSubscriber(async (newToken) => {
              try {
                config.headers['Authorization'] = `Bearer ${newToken}`;
                const retryResponse = await fetch(url, config);
                if (!retryResponse.ok) {
                  const errorData = await retryResponse.json().catch(() => ({}));
                  reject(new Error(errorData.detail || `HTTP error! status: ${retryResponse.status}`));
                  return;
                }
                const data = await retryResponse.json();
                resolve(data);
              } catch (error) {
                reject(error);
              }
            });
          });
        } else {
          window.dispatchEvent(new CustomEvent('authenticationFailed', {
            detail: { reason: 'token_expired' }
          }));
          throw new Error('Authentication failed');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Auth methods
  async login(username, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    if (response?.access_token) {
      this.setToken(response.access_token);
    }
    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }
    return response;
  }

  async register(userData) {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (response?.access_token) {
      this.setToken(response.access_token);
    }
    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }
    return response;
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async socialLogin(provider, token) {
    const response = await this.request('/auth/social', {
      method: 'POST',
      body: JSON.stringify({ provider, token }),
    });
    if (response?.access_token) {
      this.setToken(response.access_token);
    }
    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }
    return response;
  }

  async extendSession() {
    return this.refreshAccessToken();
  }

  async logout() {
    if (this.refreshToken) {
      try {
        await this.request('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: this.refreshToken })
        });
      } catch (error) {
        console.error('Error revoking refresh token:', error);
      }
    }
    this.setToken(null);
    this.setRefreshToken(null);
  }

  // User management (shared across apps)
  async getUsers(params = {}) {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return this.request(`/users${query ? `?${query}` : ''}`);
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async createUser(user) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(user)
    });
  }

  async updateUser(id, user) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(user)
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE'
    });
  }

  async changePassword(userId, passwordData) {
    return this.request(`/users/${userId}/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  }

  async unlockUser(userId) {
    return this.request(`/users/${userId}/unlock`, {
      method: 'POST'
    });
  }

  // User settings
  async getUserSettings() {
    return this.request('/user/settings');
  }

  async updateUserSettings(settings) {
    return this.request('/user/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  // Helper for polling task status
  async pollForResult(statusEndpoint, maxAttempts = 30, pollInterval = 1000) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      try {
        const status = await this.request(statusEndpoint);
        if (status.status === 'completed') {
          return status;
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Task failed');
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }
    throw new Error('Request timed out');
  }

  // Helper for progress-tracked uploads
  async requestWithProgress(endpoint, options = {}, progressCallback) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && progressCallback) {
          const progress = (event.loaded / event.total) * 100;
          progressCallback(progress);
        }
      });
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.open(options.method || 'GET', `${this.baseURL}${endpoint}`);
      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      if (!(options.body instanceof FormData)) {
        xhr.setRequestHeader('Content-Type', 'application/json');
      }
      xhr.send(options.body);
    });
  }
}

export default ApiBase;
